import { motion } from "framer-motion";
import type { ReactNode } from "react";
import { Children } from "react";
import { useReducedMotion } from "@/hooks";
import {
  staggerContainer,
  staggerItem,
  ANIMATION_CONFIG,
} from "@/lib/animations";

interface StaggeredListProps {
  children: ReactNode;
  className?: string;
  staggerDelay?: number;
  once?: boolean;
  amount?: number;
}

export function StaggeredList({
  children,
  className,
  staggerDelay = ANIMATION_CONFIG.stagger.normal,
  once = true,
  amount = 0.2,
}: StaggeredListProps) {
  const prefersReducedMotion = useReducedMotion();

  if (prefersReducedMotion) {
    return <div className={className}>{children}</div>;
  }

  const customContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: staggerDelay,
        delayChildren: 0.1,
      },
    },
  };

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once, amount }}
      variants={customContainer}
      className={className}
    >
      {Children.map(children, (child) => (
        <motion.div variants={staggerItem}>{child}</motion.div>
      ))}
    </motion.div>
  );
}

// Grid version for course cards etc.
interface StaggeredGridProps extends StaggeredListProps {
  columns?: {
    default: number;
    sm?: number;
    md?: number;
    lg?: number;
  };
  gap?: string;
}

export function StaggeredGrid({
  children,
  className,
  staggerDelay = ANIMATION_CONFIG.stagger.normal,
  once = true,
  amount = 0.1,
}: StaggeredGridProps) {
  const prefersReducedMotion = useReducedMotion();

  if (prefersReducedMotion) {
    return <div className={className}>{children}</div>;
  }

  const customContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: staggerDelay,
        delayChildren: 0.05,
      },
    },
  };

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once, amount }}
      variants={customContainer}
      className={className}
    >
      {Children.map(children, (child, index) => (
        <motion.div
          variants={{
            hidden: { opacity: 0, y: 20, scale: 0.95 },
            visible: {
              opacity: 1,
              y: 0,
              scale: 1,
              transition: {
                duration: ANIMATION_CONFIG.duration.normal,
                ease: ANIMATION_CONFIG.ease.easeOut,
              },
            },
          }}
          key={index}
        >
          {child}
        </motion.div>
      ))}
    </motion.div>
  );
}
